<template lang="html">
  <div class="vr-scene">
    <div ref="vrScene" class="vr-scene__stage" @contextmenu.prevent="onContextMenu" />
    <transition name="fade" mode="out-in" appear>
      <div v-if="!isReady" class="vr-scene__loading">
        <img v-if="isMobile() ? imageMobile : imageDesktop"
             :src="isMobile() ? imageMobile : imageDesktop"
             class="vr-scene__loading-background">
        <!-- Hide loading logo -->
        <!-- <img v-if="imageLogo" :src="imageLogo" class="vr-scene__loading-logo"> -->
        <div class="vr-scene__loading-indicator">
          <div class="vr-scene__loading-progress" :style="progressStyle" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// eslint-disable-next-line
//  import VrScene from 'panoramavr';
import VrScene from "@/lib/VrScene";

const VR_SCENE_EVENTS = [
  { name: "label-clicked", params: ["labelId"] },
  { name: "right-click", params: ["pos", "direction", "panoramaId"] },
  { name: "touch", params: [] },
  { name: "pano-icon-right-click", params: ["panoramaId", "visible"] },
  { name: "loading-panorama", params: [] },
  { name: "loaded-panorama", params: [] },
  { name: "label-icons-loaded", params: [] },
  { name: "panorama-icons-loaded", params: [] },
  { name: "error", params: ["message"] },
  { name: "on_shoted", params: ["Image"] },
  { name: "roam-move", params: ["Id"] },
  { name: "toPanoramaId", params: ["pID"] }
];

const VR_SCENE_FUNCTIONS = [
  "startLabelPlacing",
  "cancelLabelPlacing",
  "getCurrentView",
  "setLabelIcons",
  "setLabelPositions",
  "setPanoramaIcons",
  "setShowMode",
  "setPanoramaVisibility",
  "setPanoramaIcon",
  "getCurrentShot",
  "moveToRoam",
  "startRoamList",
  "moveToPanoramID",
  "getCurrentPanoramaId"
];

export default {
  name: "AiVrScene",
  props: {
    sceneId: { type: String, required: true },
    // Hook Functions
    beforeMount: { type: Function, default: () => {} },
    mounted: { type: Function, default: () => {} },
    visibleRadius:{
      type: Number,
      default: 15
    }
  },
  data: () => ({
    vrScene: null,
    isReady: false,
    imageDesktop: "",
    imageMobile: "",
    imageLogo: "",
    progressStyle: {
      "max-width": "0"
    }
  }),
  watch: {
    visibleRadius(v) {
      if(this.vrScene) {
        this.vrScene.setVisibleRadius(v)
      }
    }
  },
  computed: {},
  mounted() {
    this.initVRScene();
  },
  beforeDestroy() {
    // do something before destroying vue instance
    if (this.vrScene) this.vrScene.dispose();
  },
  methods: {
    dispose() {
      if (this.vrScene) this.vrScene.dispose();
    },
    initVRScene() {
      const options = {
        container: this.$refs.vrScene,
        sceneId: this.sceneId,
        isMobile: this.isMobile(),
        showMode: "edit",
        isAibee: true,
        mousePointerIcon: "/textures/pointer.png",
      };

      this.vrScene = new VrScene(options);

      this.registerEvents();
      this.registerFunctions();
      this.beforeMount(this.vrScene);

      this.vrScene.addEventListener("loadprogress", ({ progress }) => {
        this.progressStyle = {
          "max-width": `${Math.floor(progress * 200)}px`
        };
      });
      this.vrScene.addEventListener("loaded", () => {
        setTimeout(() => {
          this.isReady = true;
          this.$emit("ready");
        }, 600);
      });
      this.vrScene.addEventListener("error", ({ message }) => {
        this.$element.message.error(`${message}`);
      });

      this.getStoreInfo();
    },
    reloadCubeTexture(textures) {
      this.vrScene.reloadCubeTexture(textures);
    },
    moveToPanoramID(id) {
      this.vrScene.moveToPanoramID(id);
    },
    isMobile() {
      const regexp1 = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
      const regexp2 = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i;
      const target = navigator.userAgent || navigator.vendor || window.opera;
      return regexp1.test(target) || regexp2.test(target.substr(0, 4));
    },
    registerEvents() {
      VR_SCENE_EVENTS.forEach(({ name, params }) => {
        this.vrScene.addEventListener(name, ev => {
          if (!params.length) {
            this.$emit(name, this.vrScene);
          } else {
            const paramValues = {};
            params.forEach(p => {
              paramValues[p] = ev[p];
            });
            this.$emit(name, paramValues, this.vrScene);
          }
        });
      });
    },
    registerFunctions() {
      VR_SCENE_FUNCTIONS.forEach(f => {
        this[f] = (...params) => this.vrScene[f](...params);
      });
    },
    onContextMenu() {},
    mount(storeInfo) {
      this.imageMobile = storeInfo.phone_loading_picture;
      this.imageDesktop = storeInfo.desktop_loading_picture;
      this.imageLogo = storeInfo.logo;

      this.loadDataAndStart();
      // this.mounted(this.vrScene);
    },
    async loadPanoramaIcons() {
      const {
        data: {
          data: { panorama_icons: panoramaIcons }
        }
      } = await this.$http.post("/v1/list-brand-panorama-icons", {
        store_id: this.sceneId
      });
      this.vrScene.setPanoramaIcons(panoramaIcons);
    },
    async loadDataAndStart() {
      const {
        data: { data: sceneData }
      } = await this.$http.post(
        "/v1/list-panoramas?secret=U4LOTsSWEASOCXNl4lzyg0rOyGztm1so",
        { store_id: this.sceneId }
      );
      this.vrScene.init(
        sceneData.panorama_list.map(pano => {
          const p = { coordinate: {} };
          p.coordinate.positionX = pano.coordinate.position_x;
          p.coordinate.positionY = pano.coordinate.position_y;
          p.coordinate.positionZ = pano.coordinate.position_z;
          p.coordinate.directionX = pano.coordinate.direction_x;
          p.coordinate.directionY = pano.coordinate.direction_y;
          p.coordinate.directionZ = pano.coordinate.direction_z;
          p.coordinate.directionW = pano.coordinate.direction_w;
          p.coordinate.panoramaId = pano.coordinate.panorama_id;
          p.coordinate.id = pano.coordinate.id;
          p.panoramaImages = pano.panorama_images;
          p.panoramaImagesLow = pano.panorama_images_low;
          p.sequenceId = pano.seq_id;
          p.visiblePanoramaSequenceIds = pano.visible_panorama_seq_ids;
          p.visible = pano.visible;
          p.id = pano.id;
          return p;
        }),
        {
          panoramaId: sceneData.initial_view.roaming_panorama_id,
          directionX: sceneData.initial_view.roaming_direction_x,
          directionY: sceneData.initial_view.roaming_direction_y,
          directionZ: sceneData.initial_view.roaming_direction_z
        },
        sceneData.modelPath,
        sceneData.panorama_icon_id,
        null, //初始labelid
        sceneData.source
      );
      this.$emit("initialized");

      const {
        data: { data: labelIcons }
      } = await this.$http.post("/v1/list-brand-label-icons", {
        store_id: this.sceneId
      });
      this.vrScene.setLabelIcons(labelIcons);
      // console.log('this.vrScene',this.vrScene)

      const {
        data: {
          data: { labels }
        }
      } = await this.$http.post(
        "/v1/list-label-coordinates?secret=U4LOTsSWEASOCXNl4lzyg0rOyGztm1so",
        { store_id: this.sceneId }
      );

      const labelPositions = labels.map(label => {
        const l = { coordinate: {} };
        l.coordinate.positionX = label.coordinate.position_x;
        l.coordinate.positionY = label.coordinate.position_y;
        l.coordinate.positionZ = label.coordinate.position_z;
        l.coordinate.directionX = label.coordinate.direction_x;
        l.coordinate.directionY = label.coordinate.direction_y;
        l.coordinate.directionZ = label.coordinate.direction_z;
        l.coordinate.id = label.coordinate.id;
        l.panoramaId = label.panorama_id;
        l.iconId = label.icon_id;
        l.id = label.id;
        return l;
      });
      this.vrScene.setLabelPositions(labelPositions);

      this.loadPanoramaIcons();
    },
    async getStoreInfo() {
      try {
        const {
          data: { data }
        } = await this.$http.post(
          "/admin/get-store-info?secret=U4LOTsSWEASOCXNl4lzyg0rOyGztm1so",
          // { store_id: Number(this.$route.params.modelId) }
          { store_id: this.sceneId }
        );

        this.mount(data);
      } catch (error) {
        this.$log(error.error_msg);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.vr-scene {
  position: relative;

  &__stage {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
  }

  &__loading-background {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__loading-logo {
    width: 150px;
    height: 150px;
    position: absolute;
    top: calc(50% - 75px);
    left: calc(50% - 75px);
    object-fit: cover;
  }

  &__loading-indicator {
    position: absolute;
    width: 200px;
    height: 6.4px;
    top: calc(50% - 3.2px);
    left: calc(50% - 100px);
    background-color: #3c3c3c;
    border-radius: 3.2px;
  }

  &__loading-progress {
    position: absolute;
    height: 6.4px;
    width: 200px;
    top: 0;
    left: 0;
    background-color: white;
    border-radius: 3.2px;
    transition: 0.2s;
  }
}
</style>
